/**
 * Proximity voice chat tuning, shared by both sides.
 *
 * The audio itself is peer-to-peer WebRTC and never touches the server — but
 * your *voice level* does, because in this game a voice is a sound in the
 * world like any other. The mapping from measured microphone level to noise
 * emission lives here so the server's perception, the client's HUD warnings,
 * and the tests all agree on exactly how loud talking is.
 */

export const VOICE = {
  /**
   * Levels below this are treated as silence.
   *
   * Breath, room hum and keyboard rustle all leak through a hot mic. The gate
   * means only deliberate speech registers — the monster hunts talkers, not
   * people who merely own microphones.
   */
  GATE: 0.08,

  /**
   * Measured level (0..1) to noise emission.
   *
   * Calibration anchors, against tuning.ts: a murmur (~0.2) lands near a
   * crouch-walk, conversation (~0.45) lands just above a walk, and a shout
   * (~1.0) is nearly a sprint. Whispering to a nearby teammate is a
   * calculated risk; yelling across the forest is a decision.
   */
  NOISE_SCALE: 0.85,
  /** Even screaming cannot out-noise a sprint. It can only match it. */
  NOISE_MAX: 0.95,

  /**
   * Seconds a reported level stays valid on the server.
   *
   * Levels arrive at SEND_HZ; the TTL must comfortably exceed one send
   * interval or the noise floor would flicker off between packets. It also
   * bounds the damage of a client that vanishes mid-sentence: their voice
   * stops existing within a second, rather than hanging in the world forever.
   */
  LEVEL_TTL: 0.7,
  /** Client send rate for level updates. Deliberately far below tick rate. */
  SEND_HZ: 5,

  // ---- Spatialisation (client only) ---------------------------------------
  /**
   * Voices are intimate up close and gone within ~50 m — a much steeper
   * falloff than game cues, because intelligible speech at range would make
   * the radio towers pointless as meeting places and the forest small.
   */
  REF_DISTANCE: 3,
  ROLLOFF: 1.5,
  /** Voice band ceiling. Speech has nothing useful above this anyway. */
  MAX_CUTOFF: 8000,

  /** Client-side RMS smoothing and hysteresis for the speaking indicator. */
  SPEAK_ATTACK: 0.12,
  SPEAK_RELEASE: 0.35,
} as const;

/**
 * Noise emitted by a player whose microphone reads `level`.
 *
 * Pure and shared: the server feeds it into perception, and tests pin the
 * calibration so nobody retunes talking into either uselessness or suicide.
 */
export function voiceNoise(level: number): number {
  if (!Number.isFinite(level) || level < VOICE.GATE) return 0;
  return Math.min(VOICE.NOISE_MAX, level * VOICE.NOISE_SCALE);
}
